import type React from "react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useUserSettings } from "@/contexts"
import { useFormatDecimal, useFormatUnit } from "@/formatters"
import { type UnitName, languageToUnitConversionMap } from "@/models/unit"
import { GraphMenuContainer } from "@/nzds/graphs/graph-menu/graph-menu-container"
import { StackedBarGraph } from "@/nzds/graphs/stacked-bar-graph/stacked-bar-graph"
import { HStack } from "@/nzds/layout"
import {
  type IntervalDisplayUnit,
  type IntervalItem,
  type IntervalMeterIdentity,
  type IntervalStackedDataPoint,
  type IntervalStackedSeries,
  groupStackedSeriesBySource,
  meterFlowKey,
  meterStackKey,
  toIntervalMeterSource,
} from "@/services/interval"
import { useFormatFlowDirection } from "@/utils/bill/format-flow-direction"
import { inclusiveIntervalEnd } from "@/utils/date"
import { downloadCsvNew, downloadXlsxNew } from "@/utils/download"
import { getUnitNameForServerUnit } from "@/utils/unit"
import type { Interval } from "luxon"

import { Box, MenuItem, Skeleton, Stack } from "@mui/material"

import { fillIntervalChartGaps } from "./fill-interval-chart-gaps"
import { formatIntervalAxisTickLabel } from "./format-interval-bucket-label"
import { formatIntervalMeterSeriesLabel } from "./format-interval-meter-series-label"
import { formatIntervalMeterSource } from "./format-interval-meter-source"
import {
  type IntervalChartSeriesBy,
  IntervalDataChartSeriesBy,
} from "./interval-data-chart-series-by"
import { IntervalDataChartTooltip } from "./interval-data-chart-tooltip"
import { IntervalDataEmpty } from "./interval-data-empty"
import { IntervalDataGranularity } from "./interval-data-granularity"
import type { IntervalGranularity } from "./interval-data-selection"
import {
  buildIntervalExportFileName,
  intervalItemsToCsvTable,
} from "./interval-items-to-csv-table"
import type { IntervalDataEmptyVariant } from "./resolve-interval-chart-view"

const TIME_SERIES_HEIGHT = 360
const CHART_SKELETON_HEIGHT = "418px"

const toLocalizedStackedSeries = (
  series: IntervalStackedSeries[],
  formatFlowDirection: (value: string | null | undefined) => string,
  formatSource: (source: string) => string,
  seriesBy: IntervalChartSeriesBy,
  showFlowDirection: boolean
): IntervalStackedSeries[] => {
  if (seriesBy === "source") {
    return series.map((item) => ({
      ...item,
      label: formatSource(item.source),
    }))
  }
  const labels = series.map((item) =>
    formatIntervalMeterSeriesLabel(
      formatFlowDirection,
      item.flowDirection,
      item.label,
      showFlowDirection
    )
  )

  // A legend label now scopes the plot and the export, so two items that read
  // the same would leave the isolated series unidentifiable. This happens when
  // the resource hides flow direction but a meter reports both directions.
  // Re-label only the colliding items, from the original meter label.
  const labelCounts = new Map<string, number>()
  for (const label of labels) {
    labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1)
  }

  return series.map((item, index) => ({
    ...item,
    label:
      (labelCounts.get(labels[index]) ?? 0) > 1
        ? formatIntervalMeterSeriesLabel(
            formatFlowDirection,
            item.flowDirection,
            item.label,
            true
          )
        : labels[index],
  }))
}

interface IntervalDataChartProps {
  data: IntervalStackedDataPoint[]
  dateRange: Interval
  emptyVariant: IntervalDataEmptyVariant | null
  granularity: IntervalGranularity
  isLoading: boolean
  items: readonly IntervalItem[]
  meters: readonly IntervalMeterIdentity[]
  onGranularityChange: (nextGranularity: IntervalGranularity) => void
  series: IntervalStackedSeries[]
  showFlowDirection: boolean
  siteId: string
  siteName: string
  timeZone: string
  unit?: IntervalDisplayUnit
}

const resolveSelectedSeriesKey = (
  emptyVariant: IntervalDataEmptyVariant | null,
  isLoading: boolean,
  selectedSeriesKey: string | null,
  series: readonly IntervalStackedSeries[],
  seriesBy: IntervalChartSeriesBy
): string | null => {
  if (selectedSeriesKey == null || isLoading) {
    return selectedSeriesKey
  }
  if (emptyVariant) {
    return null
  }
  const legendKeys =
    seriesBy === "source"
      ? [...new Set(series.map((item) => item.source))]
      : series.map((item) => item.key)
  return legendKeys.includes(selectedSeriesKey) ? selectedSeriesKey : null
}

const itemMatchesSelectedSeries = (
  item: IntervalItem,
  metersById: Map<string, IntervalMeterIdentity>,
  selectedSeriesKey: string,
  seriesBy: IntervalChartSeriesBy
): boolean => {
  if (seriesBy === "source") {
    const meter = metersById.get(meterStackKey(item.meter_id))
    return (
      toIntervalMeterSource(meter, item.flow_direction) === selectedSeriesKey
    )
  }

  return meterFlowKey(item.meter_id, item.flow_direction) === selectedSeriesKey
}

const filterIntervalItemsByLegendSeries = (
  items: readonly IntervalItem[],
  meters: readonly IntervalMeterIdentity[],
  selectedSeriesKey: string | null,
  seriesBy: IntervalChartSeriesBy
): readonly IntervalItem[] => {
  if (selectedSeriesKey == null) {
    return items
  }

  const metersById = new Map(meters.map((meter) => [meter.id, meter]))

  return items.filter((item) =>
    itemMatchesSelectedSeries(item, metersById, selectedSeriesKey, seriesBy)
  )
}

// The legend label for the selected series, built from the same helpers the
// legend uses so the exported file names the series the way the chart does.
const resolveSelectedSeriesLabel = (
  formatFlowDirection: (value: string | null | undefined) => string,
  formatSource: (source: string) => string,
  selectedSeriesKey: string | null,
  series: IntervalStackedSeries[],
  seriesBy: IntervalChartSeriesBy,
  showFlowDirection: boolean
): string | null => {
  if (selectedSeriesKey == null) {
    return null
  }
  if (seriesBy === "source") {
    return formatSource(selectedSeriesKey)
  }
  return (
    toLocalizedStackedSeries(
      series,
      formatFlowDirection,
      formatSource,
      "meter",
      showFlowDirection
    ).find((item) => item.key === selectedSeriesKey)?.label ?? null
  )
}

const getBarCategorySpacing = (
  granularity: IntervalGranularity
): 0 | undefined => (granularity === "as_given" ? 0 : undefined)

const convertStackedDataPoint = (
  point: IntervalStackedDataPoint,
  unit: UnitName | undefined,
  unitsSystem: string
): IntervalStackedDataPoint => {
  if (!unit) {
    return point
  }

  const convert = languageToUnitConversionMap.get(unitsSystem)?.get(unit)
  if (!convert) {
    return point
  }

  const converted: IntervalStackedDataPoint = { xaxis: point.xaxis }
  for (const [key, value] of Object.entries(point)) {
    if (key === "xaxis") {
      continue
    }
    converted[key] = typeof value === "number" ? convert(value) : value
  }
  return converted
}

interface IntervalDataChartPlotProps {
  data: IntervalStackedDataPoint[]
  dateRange: Interval
  emptyVariant: IntervalDataEmptyVariant | null
  granularity: IntervalGranularity
  onSelectedSeriesKeyChange: (key: string | null) => void
  selectedSeriesKey: string | null
  series: IntervalStackedSeries[]
  seriesBy: IntervalChartSeriesBy
  showFlowDirection: boolean
  timeZone: string
  unit?: IntervalDisplayUnit
}

const IntervalDataChartPlot: React.FC<IntervalDataChartPlotProps> = ({
  data,
  dateRange,
  emptyVariant,
  granularity,
  onSelectedSeriesKeyChange,
  selectedSeriesKey,
  series,
  seriesBy,
  showFlowDirection,
  timeZone,
  unit,
}) => {
  const { t } = useTranslation("sites")
  const { formatDecimal } = useFormatDecimal(
    { maximumFractionDigits: 1 },
    { useAccountingFormatWhenNegative: false }
  )
  const { formatFlowDirection } = useFormatFlowDirection()
  const { formatUnit } = useFormatUnit()
  const { unitsSystem } = useUserSettings()
  const unitName = getUnitNameForServerUnit(unit)
  const unitLabel = formatUnit(unit)
  const plotted = useMemo(() => {
    const gappedData = fillIntervalChartGaps(
      data,
      dateRange,
      granularity,
      timeZone
    )
    if (seriesBy !== "source") {
      return { data: gappedData, meterData: gappedData, series }
    }
    return {
      ...groupStackedSeriesBySource(gappedData, series),
      meterData: gappedData,
    }
  }, [data, dateRange, granularity, series, seriesBy, timeZone])
  const convertedData = useMemo(
    () =>
      plotted.data.map((point) =>
        convertStackedDataPoint(point, unitName, unitsSystem)
      ),
    [plotted.data, unitName, unitsSystem]
  )

  if (emptyVariant) {
    return <IntervalDataEmpty variant={emptyVariant} />
  }

  const formatSource = (source: string): string =>
    formatIntervalMeterSource(source, (key) => t(key, { ns: "database" }))
  const labeledMeterSeries = toLocalizedStackedSeries(
    series,
    formatFlowDirection,
    formatSource,
    "meter",
    showFlowDirection
  )
  const labeledSeries = toLocalizedStackedSeries(
    plotted.series,
    formatFlowDirection,
    formatSource,
    seriesBy,
    showFlowDirection
  )
  const formatBucketLabel = (value: string): string =>
    formatIntervalAxisTickLabel(granularity, value, timeZone)

  return (
    <Box
      aria-label={t("intervalData.chartAriaLabel")}
      data-testid="interval-data-chart"
      role="region"
      sx={{ height: "100%", minWidth: 0, width: "100%" }}
    >
      <StackedBarGraph
        barCategoryGap={getBarCategorySpacing(granularity)}
        customTooltip={
          <IntervalDataChartTooltip
            granularity={granularity}
            meterData={plotted.meterData}
            meterSeries={labeledMeterSeries}
            series={labeledSeries}
            timeZone={timeZone}
            unit={unit}
          />
        }
        data={convertedData}
        formatBucketLabel={formatBucketLabel}
        onSelectedSeriesKeyChange={onSelectedSeriesKeyChange}
        selectedSeriesKey={selectedSeriesKey}
        series={labeledSeries}
        showVerticalGrid={false}
        splitAxisTickLines={granularity === "as_given"}
        tickFormatter={formatDecimal}
        yAxisLabel={unitLabel}
      />
    </Box>
  )
}

export const IntervalDataChart: React.FC<IntervalDataChartProps> = ({
  data,
  dateRange,
  emptyVariant,
  granularity,
  isLoading,
  items,
  meters,
  onGranularityChange,
  series,
  showFlowDirection,
  siteId,
  siteName,
  timeZone,
  unit,
}) => {
  const { t } = useTranslation("sites")
  const { formatFlowDirection } = useFormatFlowDirection()
  const [seriesBy, setSeriesBy] = useState<IntervalChartSeriesBy>("meter")
  const [selectedSeriesKey, setSelectedSeriesKey] = useState<string | null>(
    null
  )
  const resolvedSelectedSeriesKey = resolveSelectedSeriesKey(
    emptyVariant,
    isLoading,
    selectedSeriesKey,
    series,
    seriesBy
  )
  // Persist a cleared key so an empty or over-limit chart does not restore it.
  if (resolvedSelectedSeriesKey !== selectedSeriesKey) {
    setSelectedSeriesKey(resolvedSelectedSeriesKey)
  }
  const canExport = emptyVariant == null && !isLoading
  const title = t("intervalData.chartTitle")
  const selectedSeriesLabel = resolveSelectedSeriesLabel(
    formatFlowDirection,
    (source: string) =>
      formatIntervalMeterSource(source, (key) => t(key, { ns: "database" })),
    resolvedSelectedSeriesKey,
    series,
    seriesBy,
    showFlowDirection
  )
  const exportFileName = buildIntervalExportFileName(
    title,
    siteId,
    dateRange.start?.toISODate(),
    inclusiveIntervalEnd(dateRange)?.toISODate(),
    selectedSeriesLabel
  )

  const exportItems = filterIntervalItemsByLegendSeries(
    items,
    meters,
    resolvedSelectedSeriesKey,
    seriesBy
  )

  const handleDownloadCsv = () => {
    downloadCsvNew(
      intervalItemsToCsvTable(exportItems, meters, siteName, {
        formatFlowDirection,
        showFlowDirection,
      }),
      exportFileName
    )
  }

  const handleDownloadExcel = () => {
    downloadXlsxNew(
      intervalItemsToCsvTable(exportItems, meters, siteName, {
        formatFlowDirection,
        showFlowDirection,
      }),
      exportFileName
    )
  }

  return (
    <GraphMenuContainer
      ariaLabel={t("actionsMenuAriaLabel", {
        ns: "common",
        title,
      })}
      captureElement
      captureTestId="interval-data-chart-capture"
      extraItems={
        <>
          <MenuItem onClick={handleDownloadCsv}>
            {t("menuItem.exportCsv", { ns: "common" })}
          </MenuItem>
          <MenuItem onClick={handleDownloadExcel}>
            {t("menuItem.exportExcel", { ns: "common" })}
          </MenuItem>
        </>
      }
      fileName={exportFileName}
      isExportDisabled={!canExport}
      title={title}
    >
      {isLoading ? (
        <Box sx={{ height: CHART_SKELETON_HEIGHT, minWidth: 0, width: "100%" }}>
          <Skeleton
            aria-label={t("loadingAriaLabel", {
              ns: "common",
              title: t("intervalData.chartAriaLabel"),
            })}
            data-testid="interval-data-chart-skeleton"
            height="100%"
            variant="rounded"
            width="100%"
          />
        </Box>
      ) : (
        <Stack spacing={1} sx={{ minWidth: 0, width: "100%" }}>
          <HStack
            sx={{
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 2,
              minWidth: 0,
              width: "100%",
            }}
          >
            <IntervalDataGranularity
              granularity={granularity}
              onGranularityChange={onGranularityChange}
            />
            <IntervalDataChartSeriesBy
              onSeriesByChange={setSeriesBy}
              seriesBy={seriesBy}
            />
          </HStack>
          <Box sx={{ height: TIME_SERIES_HEIGHT, minWidth: 0, width: "100%" }}>
            <IntervalDataChartPlot
              data={data}
              dateRange={dateRange}
              emptyVariant={emptyVariant}
              granularity={granularity}
              onSelectedSeriesKeyChange={setSelectedSeriesKey}
              selectedSeriesKey={resolvedSelectedSeriesKey}
              series={series}
              seriesBy={seriesBy}
              showFlowDirection={showFlowDirection}
              timeZone={timeZone}
              unit={unit}
            />
          </Box>
        </Stack>
      )}
    </GraphMenuContainer>
  )
}
